import React, { useEffect, useContext, useRef } from 'react';
import { useModal } from '@nike/podium-dialog-manager';
import { SmallGlobalIcon, LanguageTunnelBtn, StyledDiv } from './styles';
import LanguageMenu from '../LanguageMenu';
import { GEO_SELECTION_QUERY_PARAM, GLOBAL_NAV_LANGUAGE_TUNNEL } from '../../constants';
import useLocale from '../../utils/hooks/localeHook';
import useBodega from '../../utils/hooks/bodegaHook';
import useFeatureFlags from '../../utils/hooks/featureFlagsHook';
import { useNavOptionsContext } from './../../context/optionsContext';
import { AnalyticsContext } from '../../context/analyticsContext';
import { getGeoMismatchCountry, isSupportedCountry } from '../../../utils/geo';

/**
 * LanguageTunnel handles the geo selection
 *  a) when the country link is clicked
 *  b) when url has the query param geoselection is set to true and nav is mounted
 */
const LanguageTunnel = () => {
  const locale = useLocale();
  const { isOpen, closeModal, openModal } = useModal();
  const { analyticsClient } = useContext(AnalyticsContext);
  const countryName = locale?.countryNames[locale.language];
  const languageTunnelEnabled = useFeatureFlags(GLOBAL_NAV_LANGUAGE_TUNNEL);
  const shouldFocusOpenLangTunnelButton = useRef(false);
  const openLangTunnelButton = useRef(false);
  const ariaLabel = `${useBodega(
    'nav_header_label_selectACountry',
    'Selected Location',
  )}: ${countryName}`;
  const { mounted } = useNavOptionsContext();

  const mismatchedCountry = getGeoMismatchCountry(locale?.country);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location?.search);
    // using renderModal here prevents us from opening the modal twice because we are rendering this component twice
    if (mounted && urlParams.get(GEO_SELECTION_QUERY_PARAM) === 'true') {
      openModal();
    }
    // if the user geoloc cookie does not match the app's locale show the modal so they can pick one
    if (mismatchedCountry && !isSupportedCountry(mismatchedCountry) && mounted) {
      openModal();
    }
  }, [mounted]);

  useEffect(() => {
    if (isOpen) {
      analyticsClient?.clickNavCountrySelectionOpen({ country: locale?.country });
    }
  }, [isOpen]);

  // handles focusing when the lang tunnel closes
  useEffect(() => {
    if (shouldFocusOpenLangTunnelButton.current) {
      shouldFocusOpenLangTunnelButton.current = false;
      openLangTunnelButton.current?.focus();
    }
  });

  return (
    <>
      <StyledDiv className="language-tunnel" data-testid="language-tunnel">
        {languageTunnelEnabled ? (
          <LanguageTunnelBtn
            Icon={<SmallGlobalIcon />}
            ariaLabel={ariaLabel}
            onClick={openModal}
            ref={openLangTunnelButton}
            title={ariaLabel}
          >
            {countryName}
          </LanguageTunnelBtn>
        ) : (
          <>
            <SmallGlobalIcon />
            {countryName}
          </>
        )}
      </StyledDiv>

      <LanguageMenu
        closeModal={closeModal}
        isOpen={isOpen}
        openModal={openModal}
        shouldFocusOpenLangTunnelButton={shouldFocusOpenLangTunnelButton}
      />
    </>
  );
};

export default LanguageTunnel;
